import {LegacyPeriodReportBoundary} from "./Layout/LegacyPeriodReportBoundary.jsx";
import React, {useContext, useEffect, useState} from "react";
import {useTranslation} from "react-i18next";
import OrderDashboardLayout from "./Layout/OrderDashboardLayout.jsx";
import PnoInTimeGraph from "./TablesAndGraphs/ByFilter/PnoInTimeGraph.jsx";
import OrdersInTimeGraph from "./TablesAndGraphs/ByFilter/OrdersInTimeGraph.jsx";
import PnoPanel from "./TablesAndGraphs/ByFilter/PnoPanel.jsx";
import * as PropTypes from "prop-types";
import ChannelGrid from "../MarketingChannelGrids/ChannelGrid.jsx";
import {GridType} from "../MarketingChannelGrids/channelGridColumnSetup.js";
import {FilterContext} from "../../../components/filters/DashboardFilterContextProvider.jsx";
import {ErpCalculationApi} from "../../../api/authority/generated/index";
import useAuthorityConfiguration from "../../../api/utilities/useAuthorityConfiguration.js";
import {useLegacyPeriodReportError} from "./Layout/LegacyPeriodReportBoundary.jsx";
import FormError from "../../../components/form/FormError.jsx";
import ReportingSnapshotStatus from "../../admin/ReportingSnapshotStatus.jsx";
import CampaignOrderBasis from "./TablesAndGraphs/ByFilter/CampaignOrderBasis.jsx";
import {isSnapshotDateUnavailable, isSnapshotPeriodIncomplete, snapshotHasPublishedDays, withSnapshotMissingDays} from "../../../utils/reportingSnapshotAvailability.js";
import {mediaSourceQualityBlocksMetrics} from "../../../utils/reportingSnapshotAvailability.js";

export function addSnapshotGapsToCampaignGraph(graph, snapshot, mediaQualitySensitive = false) {
    if (mediaQualitySensitive && mediaSourceQualityBlocksMetrics(graph?.mediaSourceQuality ?? snapshot?.mediaSourceQuality)) {
        return {...graph, dataSources: (graph?.dataSources ?? []).map((source) => ({...source, dataNet: Object.fromEntries(Object.keys(source.dataNet ?? {}).map((date) => [date, null])), dataGross: Object.fromEntries(Object.keys(source.dataGross ?? {}).map((date) => [date, null]))}))};
    }
    if (!graph || !isSnapshotPeriodIncomplete(snapshot)) {
        return graph;
    }

    const labels = withSnapshotMissingDays(snapshot, graph.labels ?? []);
    return {
        ...graph,
        labels,
        dataSources: (graph.dataSources ?? []).map((source) => {
            const fillMissing = (dailyValues) => {
                const values = {...(dailyValues ?? {})};
                for (const date of labels) {
                    if (isSnapshotDateUnavailable(snapshot, date)) {
                        values[date] = null;
                    }
                }
                return values;
            };

            return {
                ...source,
                dataNet: fillMissing(source.dataNet),
                dataGross: fillMissing(source.dataGross),
            };
        }),
    };
}

export function addSnapshotGapsToPnoComposition(composition, snapshot) {
    const quality = composition?.mediaSourceQuality ?? snapshot?.mediaSourceQuality;
    if (mediaSourceQualityBlocksMetrics(quality)) {
        const maskPno = (pno) => pno ? {...pno, pnoTotalByCurrency: [], pnoGranularByCurrency: Array.isArray(pno.pnoGranularByCurrency)
            ? pno.pnoGranularByCurrency.map((series) => ({...series, pnoEntries: Object.fromEntries(Object.keys(series?.pnoEntries ?? {}).map((date) => [date, {...series.pnoEntries[date], pnoNet: null, pnoGross: null}]))}))
            : Object.fromEntries(Object.entries(pno.pnoGranularByCurrency ?? {}).map(([key, series]) => [key, {...series, pnoEntries: Object.fromEntries(Object.keys(series?.pnoEntries ?? {}).map((date) => [date, {...series.pnoEntries[date], pnoNet: null, pnoGross: null}]))}]))} : pno;
        return composition ? {...composition, total: maskPno(composition.total), channelsOnly: maskPno(composition.channelsOnly)} : composition;
    }
    if (!composition || !isSnapshotPeriodIncomplete(snapshot)) {
        return composition;
    }

    const maskPno = (pno) => {
        if (!pno) return pno;
        const maskSeries = (series, currency) => {
            if (!series) return series;
            const existingEntries = series.pnoEntries ?? {};
            const dates = withSnapshotMissingDays(snapshot, Object.keys(existingEntries));
            const pnoEntries = Object.fromEntries(dates.map((date) => [
                date,
                isSnapshotDateUnavailable(snapshot, date) ? {currency: series.currency, pnoNet: null, pnoGross: null} : existingEntries[date] ?? {currency: series.currency, pnoNet: null, pnoGross: null},
            ]));
            return {...series, currency: series.currency ?? currency, pnoEntries};
        };
        const source = pno.pnoGranularByCurrency ?? [];
        const pnoGranularByCurrency = Array.isArray(source)
            ? source.map((series) => maskSeries(series, series?.currency))
            : Object.fromEntries(Object.entries(source).map(([currency, series]) => [currency, maskSeries(series, currency)]));

        return {...pno, pnoTotalByCurrency: [], pnoGranularByCurrency};
    };

    return {
        ...composition,
        total: maskPno(composition.total),
        channelsOnly: maskPno(composition.channelsOnly),
    };
}

export function legacyCampaignPerformanceViewState(data, error) {
    if (error !== null && error !== undefined) {
        return {kind: "error", error};
    }

    if (data === null || data === undefined) {
        return {kind: "loading"};
    }

    return {kind: "loaded", data};
}

export function legacyCampaignPerformanceErrorMessage(translatedMessage, fallbackMessage) {
    return typeof translatedMessage === "string" && translatedMessage.trim() !== ""
        ? translatedMessage
        : fallbackMessage;
}

export function LegacyCampaignPerformanceError({error}) {
    const {t} = useTranslation();
    const {et} = useLegacyPeriodReportError();
    const [translation, setTranslation] = useState({error: null, message: null});

    useEffect(() => {
        let active = true;
        et(error)
            .then((message) => {
                if (active) {
                    setTranslation({error, message});
                }
            })
            .catch(() => {
                if (active) {
                    setTranslation({error, message: ""});
                }
            });

        return () => {
            active = false;
        };
    }, [error, et]);

    const translatedMessage = translation.error === error ? translation.message : null;
    const message = legacyCampaignPerformanceErrorMessage(translatedMessage, t("validation.unknown-error"));

    return <FormError message={message}/>;
}

export function LegacyCampaignPerformanceContent({state, children}) {
    return state.kind === "error" ? <LegacyCampaignPerformanceError error={state.error}/> : children;
}

export function LegacyCampaignPerformanceChannels({project, state, snapshot}) {
    if (state.kind !== "loaded" || !snapshotHasPublishedDays(snapshot)) {
        return null;
    }

    return <ChannelGrid
        project={project}
        gridType={GridType.MarketingOrders}
        collection={state.data.channels ?? null}
        metricContext="marketing"
    />;
}

function OrderMarketingChannelOverviewContent({project}) {

    let {t} = useTranslation();
    let authorityConfiguration = useAuthorityConfiguration();

    let {since, until, centresAllowListed, domainsAllowListed} = useContext(FilterContext);
    const {customerWholesaleRequestFilter} = useContext(FilterContext);
    let sinceKey = since.format('YYYY-MM-DD');
    let untilKey = until.format('YYYY-MM-DD');

    let [topGraphsHidden, setTopGraphsHidden] = useState(true);
    let [data, setData] = useState(null);
    let [requestError, setRequestError] = useState(null);
    const requestState = legacyCampaignPerformanceViewState(data, requestError);
    const snapshot = requestState.kind === "loaded" ? requestState.data.snapshot : undefined;
    const pnoData = addSnapshotGapsToCampaignGraph(requestState.kind === "loaded" ? requestState.data.pno : null, snapshot, true);
    const orderData = addSnapshotGapsToCampaignGraph(requestState.kind === "loaded" ? requestState.data.orders : null, snapshot);
    const pnoSummary = addSnapshotGapsToPnoComposition(requestState.kind === "loaded" ? requestState.data.summary : null, snapshot);

    useEffect(() => {
        let active = true;
        const controller = new AbortController();
        setData(null);
        setRequestError(null);

        Promise.resolve().then(() => {
            if (!active || controller.signal.aborted) {
                return;
            }

            return new ErpCalculationApi(authorityConfiguration)
                .reportingLegacyCampaignPerformance({
                    projectId: project.id,
                    erpFilter: {
                        ...customerWholesaleRequestFilter,
                        since: sinceKey,
                        until: untilKey,
                        selectedCentreIds: centresAllowListed ?? [],
                        selectedDomainIds: domainsAllowListed ?? [],
                    },
                }, {signal: controller.signal})
                .then((nextData) => {
                    if (active) {
                        setData(nextData);
                    }
                })
                .catch((error) => {
                    if (active && !controller.signal.aborted) {
                        setRequestError(error);
                    }
                });
        });

        return () => {
            active = false;
            controller.abort();
        };
    }, [project.id, sinceKey, untilKey, centresAllowListed, domainsAllowListed, customerWholesaleRequestFilter]);

    // noinspection JSValidateTypes
    return <OrderDashboardLayout title={t('route.campaign-performance')} showMarketingFilters={true} showModeSwitch={false}>
        <LegacyCampaignPerformanceContent state={requestState}>
        <ReportingSnapshotStatus snapshots={snapshot} mediaSourceQuality={requestState.kind === "loaded" ? requestState.data.pno?.mediaSourceQuality ?? requestState.data.summary?.mediaSourceQuality : null}/>
        <div className="col-span-12 mb-2"><CampaignOrderBasis isMarketing={true}/></div>

        <PnoInTimeGraph
            project={project}
            metricContext="marketing"
            defaultHidden={true}
            hidden={topGraphsHidden}
            setHidden={setTopGraphsHidden}
            showHideButton={true}
            data={pnoData ?? null}
        />

        <OrdersInTimeGraph
            project={project}
            metricContext="marketing"
            defaultHidden={true}
            hidden={topGraphsHidden}
            setHidden={setTopGraphsHidden}
            showHideButton={true}
            data={orderData ?? null}
        />

        <PnoPanel project={project} halfSize={true} compositionData={pnoSummary ?? null}/>
        <LegacyCampaignPerformanceChannels project={project} state={requestState} snapshot={snapshot}/>
        </LegacyCampaignPerformanceContent>
    </OrderDashboardLayout>;
}

export default function OrderMarketingChannelOverview(props) {
    return <LegacyPeriodReportBoundary project={props.project}><OrderMarketingChannelOverviewContent {...props}/></LegacyPeriodReportBoundary>;
}
